import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/integrations/auth/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle, Loader2, Terminal, XCircle } from "lucide-react";
import logoLivemotion from "@/assets/logo-livemotion.png";

type Step =
  | { kind: "enter" }
  | { kind: "checking" }
  | { kind: "confirm"; userCode: string }
  | { kind: "submitting"; userCode: string }
  | { kind: "approved" }
  | { kind: "denied" };

function normalizeUserCode(value: string) {
  return value.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

function formatUserCode(value: string) {
  const code = normalizeUserCode(value);
  return code.length > 4 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

function errorMessage(error: unknown) {
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    if (typeof record.error_description === "string") {
      return record.error_description;
    }
    if (typeof record.message === "string" && record.message) {
      return record.message;
    }
  }
  return "Something went wrong. Please try again.";
}

export default function DevicePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get("user_code") ?? "";
  const [code, setCode] = useState(formatUserCode(initialCode));
  const [step, setStep] = useState<Step>({ kind: "enter" });
  const [error, setError] = useState<string | null>(null);

  const verify = async (value: string) => {
    const userCode = normalizeUserCode(value);
    if (!userCode) return;
    setError(null);
    setStep({ kind: "checking" });
    try {
      const { data, error } = await authClient.device({
        query: { user_code: userCode },
      });
      if (error || !data) {
        setError(errorMessage(error));
        setStep({ kind: "enter" });
        return;
      }
      if (data.status !== "pending") {
        setError("This code has already been used.");
        setStep({ kind: "enter" });
        return;
      }
      setStep({ kind: "confirm", userCode });
    } catch (error) {
      setError(errorMessage(error));
      setStep({ kind: "enter" });
    }
  };

  useEffect(() => {
    if (initialCode) void verify(initialCode);
    // Only verify the code from the link once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (userCode: string, approve: boolean) => {
    setError(null);
    setStep({ kind: "submitting", userCode });
    try {
      const { error } = approve
        ? await authClient.device.approve({ userCode })
        : await authClient.device.deny({ userCode });
      if (error) {
        setError(errorMessage(error));
        setStep({ kind: "enter" });
        return;
      }
      setStep({ kind: approve ? "approved" : "denied" });
    } catch (error) {
      setError(errorMessage(error));
      setStep({ kind: "enter" });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src={logoLivemotion}
              alt="LiveMotion"
              className="h-14 object-contain"
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">LM Hub</h1>
          <p className="text-sm text-muted-foreground mt-2">Connect a device</p>
        </div>

        <Card className="border-border/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-lg">
              Authorize command-line access
            </CardTitle>
            <CardDescription>
              A script or terminal is asking to act as{" "}
              <span className="font-medium text-foreground">{user?.email}</span>
              . Only approve codes you started yourself.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step.kind === "approved" ? (
              <div className="text-center py-6 space-y-3">
                <CheckCircle className="h-12 w-12 text-success mx-auto" />
                <div>
                  <p className="font-medium">Device connected</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    You can close this tab and return to your terminal.
                  </p>
                </div>
              </div>
            ) : step.kind === "denied" ? (
              <div className="text-center py-6 space-y-3">
                <XCircle className="h-12 w-12 text-destructive mx-auto" />
                <div>
                  <p className="font-medium">Request denied</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    The device did not get access.
                  </p>
                </div>
              </div>
            ) : step.kind === "confirm" || step.kind === "submitting" ? (
              <div className="space-y-4">
                <div className="rounded-md border border-border bg-muted px-4 py-3 text-center font-mono text-2xl tracking-widest">
                  {formatUserCode(step.userCode)}
                </div>
                <p className="text-sm text-muted-foreground">
                  Check that this code matches the one shown in your terminal.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    disabled={step.kind === "submitting"}
                    onClick={() => void decide(step.userCode, false)}
                  >
                    Deny
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={step.kind === "submitting"}
                    onClick={() => void decide(step.userCode, true)}
                  >
                    {step.kind === "submitting" && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    Approve
                  </Button>
                </div>
              </div>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  void verify(code);
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="user-code">Code</Label>
                  <Input
                    id="user-code"
                    placeholder="ABCD-EFGH"
                    value={code}
                    onChange={(e) => setCode(formatUserCode(e.target.value))}
                    disabled={step.kind === "checking"}
                    autoComplete="off"
                    autoFocus
                    className="font-mono tracking-widest"
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={step.kind === "checking" || !code}
                >
                  {step.kind === "checking" ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Terminal className="h-4 w-4 mr-2" />
                  )}
                  Continue
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
